import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@sharpana/auth/authHooks";
import * as Sentry from "@sentry/react";
import { updateUserCategories } from "../RecommendationFeed/api_client/recommendation_api";
import {
  User,
  MIN_CATEGORIES_REQUIRED,
  CATEGORY_GROUPS,
  ContentCategory,
  validateCategories,
} from "./onboardingUtils";
import {
  onboardingContainer,
  onboardingTitle,
  onboardingSubtitle,
  categoriesContainer,
  categoryGroupContainer,
  categoryGroupTitle,
  categoriesGrid,
  categoryButton,
  categoryButtonSelected,
  buttonContainer,
  continueButton,
  errorMessage,
  stepIndicator,
} from "./onboarding_styles";

// Use the shared category groups configuration
const categoryGroups = CATEGORY_GROUPS;

// Screen reader only class
const srOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: "0",
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: "0",
} as const;

export function CategorySelection() {
  const user = useUser() as User | null;
  const [selectedCategories, setSelectedCategories] = useState<
    ContentCategory[]
  >([]);
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const lastKeyPressTime = useRef<number>(0);
  const selectedCount = selectedCategories.length;

  // Initialize with user's existing categories
  useEffect(() => {
    if (user) {
      if (user.feed_categories && Array.isArray(user.feed_categories)) {
        setSelectedCategories(user.feed_categories);
      }
      setIsLoading(false);
    }
  }, [user]);

  // Clear error when categories change
  useEffect(() => {
    setError("");
  }, [selectedCategories]);

  // Toggle category selection
  const toggleCategory = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent, category: ContentCategory) => {
      e.preventDefault(); // Prevent any form submission
      setSelectedCategories((prev) => {
        if (prev.includes(category)) {
          return prev.filter((c) => c !== category);
        } else {
          return [...prev, category];
        }
      });
    },
    []
  );

  // Handle keyboard navigation
  const handleKeyPress = useCallback(
    (
      event: React.KeyboardEvent<HTMLButtonElement>,
      category: ContentCategory,
      groupIndex: number,
      categoryIndex: number
    ) => {
      const now = Date.now();
      const target = event.target as HTMLElement;
      const grid = target.closest(`.${categoriesGrid}`);

      if (event.key === " " || event.key === "Enter") {
        // Prevent double-firing of events
        if (now - lastKeyPressTime.current > 200) {
          toggleCategory(event, category);
          lastKeyPressTime.current = now;
        }
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        const nextButton = target.nextElementSibling as HTMLButtonElement;
        if (nextButton) {
          nextButton.focus();
        } else if (grid) {
          // Move to first button of next group
          const nextGroup =
            grid.parentElement?.nextElementSibling?.querySelector(
              `.${categoriesGrid}`
            );
          const firstButton = nextGroup?.querySelector("button");
          firstButton?.focus();
        }
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        const prevButton = target.previousElementSibling as HTMLButtonElement;
        if (prevButton) {
          prevButton.focus();
        } else if (grid) {
          // Move to last button of previous group
          const prevGroup =
            grid.parentElement?.previousElementSibling?.querySelector(
              `.${categoriesGrid}`
            );
          const buttons = prevGroup?.querySelectorAll("button");
          buttons?.[buttons.length - 1]?.focus();
        }
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        // Find the button in the same column of the next group
        const nextGroup =
          grid?.parentElement?.nextElementSibling?.querySelector(
            `.${categoriesGrid}`
          );
        if (nextGroup) {
          const buttons = nextGroup.querySelectorAll("button");
          buttons[categoryIndex]?.focus();
        }
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        // Find the button in the same column of the previous group
        const prevGroup =
          grid?.parentElement?.previousElementSibling?.querySelector(
            `.${categoriesGrid}`
          );
        if (prevGroup) {
          const buttons = prevGroup.querySelectorAll("button");
          buttons[categoryIndex]?.focus();
        }
      } else if (event.key === "Home") {
        event.preventDefault();
        // Move to first button in the grid
        grid?.querySelector("button")?.focus();
      } else if (event.key === "End") {
        event.preventDefault();
        // Move to last button in the grid
        const buttons = grid?.querySelectorAll("button");
        buttons?.[buttons.length - 1]?.focus();
      }
    },
    [toggleCategory]
  );

  // Submit selected categories
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Clear any existing errors
    setError("");

    // Validate categories before submission
    const validation = validateCategories(selectedCategories);
    if (validation.tooFew) {
      setError(
        `Please select at least ${MIN_CATEGORIES_REQUIRED} categories to continue`
      );
      return;
    }
    if (validation.invalidCategories.length > 0) {
      const errorMessage = "Invalid categories detected during submission";
      setError(
        `Some selected categories are invalid, we have been alerted and will fix this soon! In the meantime please remove: ${validation.invalidCategories.join(
          ", "
        )}`
      );
      console.error("Invalid categories:", validation.invalidCategories);
      Sentry.captureException(new Error(errorMessage), {
        tags: {
          component: "CategorySelection",
          action: "submit",
        },
        extra: {
          invalidCategories: validation.invalidCategories,
          allSelectedCategories: selectedCategories,
          user_id: user?.id,
        },
      });
      return;
    }

    // Prevent duplicate submission
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await updateUserCategories(selectedCategories);
      if (response.success) {
        // Successfully updated categories, navigate to item ranking step
        navigate("/onboarding/rank-items");
      } else {
        setError(
          response.error || "Failed to update categories. Please try again."
        );
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Error updating categories:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render content while loading
  if (isLoading) {
    return (
      <div className={onboardingContainer} role="main">
        <h1 className={onboardingTitle}>Loading your preferences...</h1>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={onboardingContainer} role="main">
      <div className={stepIndicator}>
        Step 1 of 2: Select Categories
      </div>
      <h1 className={onboardingTitle}>Welcome to Sharpana!</h1>
      <p className={onboardingSubtitle}>
        <strong>Let's craft your feed.</strong> Pick at least{" "}
        {MIN_CATEGORIES_REQUIRED} topics you want to follow.
      </p>

      <div
        className={categoriesContainer}
        role="group"
        aria-label="Content Categories"
        aria-describedby="category-instructions category-keyboard-instructions"
        data-testid="category-selection-container"
      >
        <div
          id="category-keyboard-instructions"
          style={srOnly}
          data-testid="keyboard-instructions"
        >
          Use arrow keys to navigate between categories. Press Space or Enter to
          select or deselect a category. Use Home and End keys to jump to the
          first or last category in a group. There is no limit to how many
          categories you can select.
        </div>
        <p id="category-instructions" style={srOnly}>
          Select at least {MIN_CATEGORIES_REQUIRED} categories. Selected
          categories will be used to personalize your feed.
        </p>
        {categoryGroups.map((group, groupIndex) => {
          const groupId = `group-${group.title
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "-")}`;
          return (
            <div key={group.title} className={categoryGroupContainer}>
              <h2 className={categoryGroupTitle} id={groupId}>
                {group.title}
              </h2>
              <div
                className={categoriesGrid}
                role="group"
                aria-labelledby={groupId}
              >
                {group.categories.map((category, categoryIndex) => {
                  const isSelected = selectedCategories.includes(category);
                  const isDisabled = false;
                  return (
                    <button
                      type="button"
                      key={category}
                      className={`${categoryButton} ${
                        isSelected ? categoryButtonSelected : ""
                      }`}
                      onClick={(e) => toggleCategory(e, category)}
                      onKeyDown={(e) =>
                        handleKeyPress(e, category, groupIndex, categoryIndex)
                      }
                      tabIndex={0}
                      aria-pressed={isSelected}
                      aria-label={`${category}${
                        isSelected ? " (selected)" : ""
                      }`}
                      data-testid={`category-button-${category
                        .toLowerCase()
                        .replace(/[^a-z0-9]/g, "-")}`}
                      title={category}
                    >
                      {category}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <p className={errorMessage} role="alert" aria-live="polite">
          {error}
        </p>
      )}

      <div className={buttonContainer}>
        <button
          type="submit"
          className={continueButton(selectedCount >= MIN_CATEGORIES_REQUIRED)}
          disabled={isSubmitting || selectedCount < MIN_CATEGORIES_REQUIRED}
          aria-busy={isSubmitting}
          aria-disabled={
            isSubmitting || selectedCount < MIN_CATEGORIES_REQUIRED
          }
          data-testid="continue-button"
        >
          {isSubmitting
            ? "Saving..."
            : selectedCount < MIN_CATEGORIES_REQUIRED
            ? `Select at least ${MIN_CATEGORIES_REQUIRED} categories (${selectedCount} selected)`
            : `Continue (${selectedCount} selected)`}
        </button>
      </div>
    </form>
  );
}

export default CategorySelection;
